import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID, Service, inject } from '@angular/core';

@Service()
export class LocalStorageService {
  private readonly _platformId: object;

  private _serverStorage = new Map<string, string>();

  constructor() {
    this._platformId = inject(PLATFORM_ID);
  }

  setItem(key: string, value: string): void {
    if (isPlatformBrowser(this._platformId)) {
      try {
        localStorage.setItem(key, value);
      } catch {
        this._serverStorage.set(key, value);
      }
    } else {
      this._serverStorage.set(key, value);
    }
  }

  getItem(key: string): string | null {
    if (isPlatformBrowser(this._platformId)) {
      try {
        return localStorage.getItem(key);
      } catch {
        return this._serverStorage.get(key) ?? null;
      }
    } else {
      return this._serverStorage.get(key) ?? null;
    }
  }

  removeItem(key: string): void {
    if (isPlatformBrowser(this._platformId)) {
      try {
        localStorage.removeItem(key);
      } catch {
        this._serverStorage.delete(key);
      }
    } else {
      this._serverStorage.delete(key);
    }
  }

  clear(): void {
    if (isPlatformBrowser(this._platformId)) {
      try {
        localStorage.clear();
      } catch {
        this._serverStorage.clear();
      }
    } else {
      this._serverStorage.clear();
    }
  }

  setSessionItem(key: string, value: string): void {
    if (isPlatformBrowser(this._platformId)) {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        this._serverStorage.set(`session_${key}`, value);
      }
    } else {
      this._serverStorage.set(`session_${key}`, value);
    }
  }

  getSessionItem(key: string): string | null {
    if (isPlatformBrowser(this._platformId)) {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return this._serverStorage.get(`session_${key}`) ?? null;
      }
    } else {
      return this._serverStorage.get(`session_${key}`) ?? null;
    }
  }

  removeSessionItem(key: string): void {
    if (isPlatformBrowser(this._platformId)) {
      try {
        sessionStorage.removeItem(key);
      } catch {
        this._serverStorage.delete(`session_${key}`);
      }
    } else {
      this._serverStorage.delete(`session_${key}`);
    }
  }

  clearSession(): void {
    if (isPlatformBrowser(this._platformId)) {
      try {
        sessionStorage.clear();
      } catch {
        Array.from(this._serverStorage.keys()).forEach(key => {
          if (key.startsWith('session_')) {
            this._serverStorage.delete(key);
          }
        });
      }
    } else {
      Array.from(this._serverStorage.keys()).forEach(key => {
        if (key.startsWith('session_')) {
          this._serverStorage.delete(key);
        }
      });
    }
  }
}
